import { i18n } from "@/services/locale/i18n";
import { promiseWithMinimumDelay } from "@/utils/promiseWithMinimumDelay";

import { Interlude } from "../../Interlude";
import { calculateLeadScore } from "../../visitor-checks/calculateLeadScore";
import { calculateLssQualification } from "../../visitor-checks/calculateLssQualification";
import { checkAttorneyAvailability } from "../../visitor-checks/checkAttorneyAvailability";
import { checkCmnEligibility } from "../../visitor-checks/checkCmnEligibility";
import { getVisitorStatusCode } from "../../visitor-checks/getVisitorStatusCode";
import { interludeContent } from "../config.common";
import { DELAY_MS, stepsNames } from "../config.const";
import type { RenderInterludeArgs, SubPractices } from "../config.types";

import { isFamilyDirectToLssFlow } from "./common-family";

export const familyDivorce: SubPractices = {
  firstScreen: "service-type",
  screens: {
    "service-type": {
      next: async () => "zip-code",
    },
    "zip-code": {
      next: async ({ answers, experiments }) => {
        const { abTestAttorneyAvailability } = experiments;
        const { skip, state, county } = answers;

        if (skip) {
          return "state-selection";
        }

        const isAttorneyAvailable =
          abTestAttorneyAvailability && county
            ? await checkAttorneyAvailability()
            : true;

        if (state === "unsupported" || !isAttorneyAvailable) {
          return "unsupported-county";
        }

        return "location-confirmation";
      },
    },
    "state-selection": {
      next: async ({ answers }) => {
        const { state } = answers;

        if (state === "unsupported") {
          return "unsupported";
        }

        return "location-confirmation";
      },
    },
    unsupported: {
      next: async () => "unsupported-confirmed",
      sanitize: true,
    },
    "unsupported-confirmed": {
      next: async () => "unsupported-confirmed",
    },
    "location-confirmation": {
      next: async () => "case-urgency",
    },
    "case-urgency": {
      next: async () => "more-info",
    },
    "more-info": {
      next: async ({ answers, experiments }) => {
        const { abTestCmnMidIntent } = experiments;
        calculateLeadScore(answers, stepsNames["more-info"]);

        if (abTestCmnMidIntent) {
          checkCmnEligibility({ answers });
        }

        if (
          isFamilyDirectToLssFlow({
            answers,
            experiments,
          })
        ) {
          const isQualifiedForLss = await promiseWithMinimumDelay(
            calculateLssQualification(answers),
            DELAY_MS,
          );

          if (isQualifiedForLss) return "has-minor-children";
        }

        return "create-meeting";
      },
      renderInterlude: ({ answers, experiments }: RenderInterludeArgs) => {
        return isFamilyDirectToLssFlow({ answers, experiments }) ? (
          <Interlude heading={i18n.t("FUNNEL_INTERLUDE_JUST_A_FEW_MOMENTS")} />
        ) : null;
      },
    },
    "create-meeting": {
      next: async ({ answers }) =>
        answers?.eventStartTime === "call_me_now"
          ? "contact-info-call-now"
          : "contact-info",
    },
    "contact-info": {
      next: async ({ answers }) => {
        await promiseWithMinimumDelay(
          calculateLssQualification(answers),
          DELAY_MS,
        );
        return "intake-confirmation";
      },
      sanitize: true,
      renderInterlude: () => (
        <Interlude heading={i18n.t("FUNNEL_INTERLUDE_CONFIRMING_YOUR_CALL")} />
      ),
      isAuth: true,
    },

    "contact-info-call-now": {
      next: async () => "intake-confirmation-call-now",
      sanitize: true,
      renderInterlude: () => (
        <Interlude heading={i18n.t("FUNNEL_INTERLUDE_CONFIRMING_YOUR_CALL")} />
      ),
      isAuth: true,
    },
    "contact-info-dlss": {
      next: async () => {
        return "lss-checkout";
      },
      sanitize: true,
      isAuth: true,
    },
    "intake-confirmation": {
      next: async () => "lss-intro",
    },
    "intake-confirmation-call-now": {
      next: async () => undefined,
    },
    "lss-intro": {
      next: async () => "has-minor-children",
    },

    // Digital Intake steps
    "has-minor-children": {
      next: async ({ answers }) => {
        const { hasMinorChildren } = answers;
        if (hasMinorChildren === "Yes") return "children-info";
        if (hasMinorChildren === "No") return "opposing-party";
      },
      isUpdate: true,
    },
    "children-info": {
      next: async () => "children-safety",
    },
    "children-safety": {
      next: async () => "child-protective-services",
    },
    "child-protective-services": {
      next: async () => "opposing-party",
    },
    "opposing-party": {
      next: async () => "has-upcoming-court-event",
    },
    "has-upcoming-court-event": {
      next: async ({ answers }) => {
        const { hasUpcomingCourtEvent } = answers;
        if (hasUpcomingCourtEvent === "Yes") return "upcoming-event-date";
        if (hasUpcomingCourtEvent === "No") return "currently-represented";
        if (hasUpcomingCourtEvent === "I have a different event")
          return "upcoming-event-date";
      },
    },
    "upcoming-event-date": {
      next: async () => "currently-represented",
    },
    "currently-represented": {
      next: async ({ answers }) => {
        const statusCodePromise = getVisitorStatusCode({
          answers,
          currentStep: stepsNames["currently-represented"],
        });

        const statusCode = await promiseWithMinimumDelay(
          statusCodePromise,
          answers?.moreInfo ? DELAY_MS : 0,
        );

        if (statusCode) {
          answers.visitorStatusCode = statusCode;
          if (statusCode !== "ELG1" && statusCode !== "ERROR") {
            return "out-of-scope";
          }
        }
        return answers.moreInfo ? "book-lss" : "additional-info";
      },
      isUpdate: true,
      renderInterlude: ({ answers, experiments }: RenderInterludeArgs) => {
        const { heading, subHeading } = isFamilyDirectToLssFlow({
          answers,
          experiments,
        })
          ? interludeContent.directToLss
          : interludeContent.default;

        return answers?.moreInfo ? (
          <Interlude heading={heading} subHeading={subHeading} />
        ) : null;
      },
    },
    "additional-info": {
      next: async ({ answers }) => {
        const statusCodePromise = getVisitorStatusCode({
          answers,
          currentStep: stepsNames["additional-info"],
        });

        const statusCode = await promiseWithMinimumDelay(
          statusCodePromise,
          answers?.moreInfo ? DELAY_MS : 0,
        );

        if (statusCode) {
          answers.visitorStatusCode = statusCode;
        }

        return "book-lss";
      },
      isUpdate: true,
      renderInterlude: ({ answers, experiments }: RenderInterludeArgs) => {
        const { heading, subHeading } = isFamilyDirectToLssFlow({
          answers,
          experiments,
        })
          ? interludeContent.directToLss
          : interludeContent.default;

        return <Interlude heading={heading} subHeading={subHeading} />;
      },
    },
    // Direct LSS steps
    "book-lss": {
      next: async ({ answers, experiments }) => {
        if (isFamilyDirectToLssFlow({ answers, experiments })) {
          return "contact-info-dlss";
        }
        return "lss-checkout";
      },
    },
    "lss-checkout": {
      next: async ({ answers, meta }) => {
        const { skip } = answers;

        if (skip) return "create-meeting";

        const { checkoutStatus } = meta;
        return checkoutStatus;
      },
    },
    "slot-is-taken": {
      next: async () => "book-lss",
    },
    "payment-declined": {
      next: async () => "lss-checkout",
    },
    "lss-confirmation": {
      next: async () => undefined,
    },
    "out-of-scope": {
      next: async () => undefined,
    },
    "unsupported-county": {
      next: async () => undefined,
    },
  },
};
